import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from '@/components/ui';
import { closePcrWithoutWorkId } from '../../../services/pcrService';
import type { PcrSubmission } from '../../../services/pcrService';

export interface PcrCloseWithoutWorkModalProps {
  visible: boolean;
  row: PcrSubmission | null;
  onClose: () => void;
  onSuccess: (updated: PcrSubmission) => void;
}

const PcrCloseWithoutWorkModal: React.FC<PcrCloseWithoutWorkModalProps> = ({
  visible,
  row,
  onClose,
  onSuccess,
}) => {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      setNote('');
      setError(null);
    }
  }, [visible]);

  const handleSubmit = async () => {
    if (!row) return;
    setSubmitting(true);
    setError(null);
    try {
      const updated = await closePcrWithoutWorkId(row.id, note.trim() || undefined);
      onSuccess(updated);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to close PCR');
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible || !row) return null;

  return (
    // `autoHeight`: this is a one-paragraph confirm plus a short note field, not a form that
    // needs the standard fixed-height treatment — without it, the `lg` size pins to 85vh on
    // desktop and renders as a mostly-empty sheet (the "cancel PCR has a lot of whitespace" report).
    <Modal show={visible} onHide={() => !submitting && onClose()} size="lg" autoHeight>
      <Modal.Header closeButton>
        <Modal.Title>Close PCR without Work ID</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="tw-text-content-muted tw-text-sm tw-mb-3">
          Finalize this PCR when no maintenance work is required (for example after field assessment). The submission
          will be marked <strong>Finalized</strong>.
        </p>
        {error ? (
          <Alert variant="danger">
            {error}
          </Alert>
        ) : null}
        <Form.Group>
          <Form.Label>Optional note (appears in PCR history)</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. No action required — debris already cleared."
            disabled={submitting}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button type="button" variant="primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Closing…' : 'Close without Work ID'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PcrCloseWithoutWorkModal;
